import { motion, useReducedMotion } from 'framer-motion';
import type { ReactNode } from 'react';

export function GlassCard({
  children,
  className = '',
  delay = 0,
  id,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  id?: string;
}) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      id={id}
      initial={reduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={
        reduceMotion
          ? { duration: 0 }
          : { duration: 0.35, ease: [0.25, 0.1, 0.25, 1], delay }
      }
      className={`card-interactive glass-panel min-w-0 overflow-x-hidden rounded-2xl p-4 shadow-card sm:rounded-3xl sm:p-5 ${className}`}
    >
      {children}
    </motion.div>
  );
}

export function PageHeader({
  title,
  subtitle,
  compact = true,
}: {
  title: string;
  subtitle?: string;
  /** Calmer typography + less vertical space (default). */
  compact?: boolean;
}) {
  return (
    <div className={`min-w-0 px-0.5 ${compact ? 'mb-4 sm:mb-5' : 'mb-6 sm:mb-8'}`}>
      <h1
        className={`font-display font-semibold tracking-tight text-white ${
          compact ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-3xl md:text-4xl'
        }`}
      >
        {title}
      </h1>
      {subtitle ? (
        <p
          className={`max-w-lg text-pretty text-zinc-500 ${
            compact ? 'mt-1 text-xs leading-relaxed sm:text-[0.8125rem]' : 'mt-2 text-sm text-zinc-400 sm:text-[0.9375rem] md:text-base'
          }`}
        >
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

export function ProgressBar({
  value,
  max,
  colorClass = 'bg-accent',
}: {
  value: number;
  max: number;
  colorClass?: string;
}) {
  const reduceMotion = useReducedMotion();
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="h-2.5 w-full overflow-hidden rounded-full bg-zinc-800 sm:h-2">
      <motion.div
        className={`h-full rounded-full ${colorClass}`}
        initial={reduceMotion ? { width: `${pct}%` } : { width: 0 }}
        animate={{ width: `${pct}%` }}
        transition={
          reduceMotion
            ? { duration: 0 }
            : { duration: 0.65, ease: [0.25, 0.1, 0.25, 1] }
        }
      />
    </div>
  );
}

export function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-zinc-300">
      {children}
    </span>
  );
}

/** Polished empty block — use inside GlassCard or standalone. */
export function EmptyStateCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-dashed border-white/15 bg-white/[0.03] px-4 py-8 text-center sm:px-6">
      <p className="text-sm font-semibold text-zinc-200">{title}</p>
      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-zinc-500">{description}</p>
      {children ? <div className="mt-5 flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:justify-center">{children}</div> : null}
    </div>
  );
}
